import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { InitialsAvatar } from "../../components/InitialsAvatar";

interface AllocationRow {
    shift_id: string;
    billed_service_id: string | null;
    travel_km: string | null;
    service_date: string;
    starts: string;
    ends: string;
    hours: string;
    rate_category: string;
    employee_name: string | null;
    participant_id: string;
    participant_name: string;
    pricing_category: string | null;
    pricing_state: string | null;
    company_name: string | null;
    role_name: string;
    role_rate: string;
    service_name: string | null;
    service_rate: string | null;
    charge_percent: string | null;
    support_item_number: string | null;
    support_item_name: string | null;
    catalogue_price: string | null;
    applied_rate: string;
    invoiced: boolean;
}

interface ServiceRow {
    id: string; name: string; is_active: boolean;
    ndis_item_number: string | null; default_rate: string | null; charge_percent: string | null;
}

const money = (value: string | number | null) =>
    value == null ? "—" : `$${Number(value).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The allocation screen: every delivered shift, the service it is billed as,
 * the NDIS support item behind that service and the rate that will actually
 * apply. Allocate here, then generate the invoice from Invoices — one shift
 * can only ever land on one invoice (the database holds that line unique).
 */
export const Allocations = () => {
    const [filters, setFilters] = useState<{ participantId: string; employeeId: string; from: string; to: string }>(
        { participantId: "", employeeId: "", from: "", to: "" });

    const query = useMemo(() => {
        const params = new URLSearchParams();
        if (filters.participantId) params.set("participantId", filters.participantId);
        if (filters.employeeId) params.set("employeeId", filters.employeeId);
        if (filters.from) params.set("from", filters.from);
        if (filters.to) params.set("to", filters.to);
        const s = params.toString();
        return s ? `?${s}` : "";
    }, [filters]);

    const allocations = useResource<AllocationRow[]>(`/allocations${query}`);
    const services = useResource<ServiceRow[]>("/services");
    const participants = useResource<Array<{ id: string; full_name: string }>>("/participants");
    const employees = useResource<Array<{ id: string; full_name: string }>>("/employees");

    const [openId, setOpenId] = useState<string | null>(null);
    const [draft, setDraft] = useState<{ serviceId: string; travelKm: string }>({ serviceId: "", travelKm: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const rows = allocations.data ?? [];
    const uninvoiced = rows.filter((r) => !r.invoiced);
    const estimate = uninvoiced.reduce((sum, r) => sum + Number(r.hours) * Number(r.applied_rate), 0);

    const open = (row: AllocationRow) => {
        setOpenId(row.shift_id);
        setDraft({ serviceId: row.billed_service_id ?? "", travelKm: row.travel_km ?? "" });
        setError(null);
    };

    const save = async (row: AllocationRow) => {
        setBusy(true);
        setError(null);
        try {
            await api.put(`/shifts/${row.shift_id}/allocation`, {
                serviceId: draft.serviceId || null,
                travelKm: draft.travelKm === "" ? null : Number(draft.travelKm),
            });
            setOpenId(null);
            allocations.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the allocation");
        } finally {
            setBusy(false);
        }
    };

    if (allocations.error) return <div className="error-banner">{allocations.error}</div>;

    const selectedService = (id: string) => (services.data ?? []).find((s) => s.id === id) ?? null;

    return (
        <div style={{ display: "grid", gap: "0.9rem" }}>
            <div className="card">
                <div className="card-body" style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div className="field" style={{ minWidth: 200, marginBottom: 0 }}>
                        <label htmlFor="al-participant">Participant</label>
                        <select id="al-participant" value={filters.participantId}
                            onChange={(e) => setFilters({ ...filters, participantId: e.target.value })}>
                            <option value="">All participants</option>
                            {(participants.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                        </select>
                    </div>
                    <div className="field" style={{ minWidth: 200, marginBottom: 0 }}>
                        <label htmlFor="al-worker">Worker</label>
                        <select id="al-worker" value={filters.employeeId}
                            onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}>
                            <option value="">All workers</option>
                            {(employees.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                        </select>
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                        <label htmlFor="al-from">Delivered from</label>
                        <input id="al-from" type="date" value={filters.from}
                            onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                        <label htmlFor="al-to">To</label>
                        <input id="al-to" type="date" value={filters.to}
                            onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
                    </div>
                    <div className="spacer" />
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "0.7rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            Uninvoiced estimate
                        </div>
                        <div className="mono" style={{ fontSize: "1.15rem", fontWeight: 700 }}>{money(estimate)}</div>
                    </div>
                    <Link className="btn" to="/crm/invoices">Redirect to invoice generation ↗</Link>
                </div>
            </div>

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Participant</th><th>Worker</th><th>Delivered</th><th>Hours</th>
                                    <th>Service</th><th>Applied /h</th><th>Status</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <Fragment key={row.shift_id}>
                                        <tr className="clickable" onClick={() => (openId === row.shift_id ? setOpenId(null) : open(row))}>
                                            <td>
                                                <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600 }}>
                                                    <InitialsAvatar name={row.participant_name} size={24} />
                                                    {row.participant_name}
                                                </span>
                                                {row.company_name && <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{row.company_name}</div>}
                                            </td>
                                            <td>{row.employee_name ?? "—"}</td>
                                            <td className="mono">{formatDate(row.service_date)} {row.starts}–{row.ends}</td>
                                            <td className="mono">{row.hours}</td>
                                            <td>
                                                {row.service_name ?? <span style={{ color: "var(--muted)" }}>Not allocated</span>}
                                                {row.support_item_number && (
                                                    <div className="mono" style={{ fontSize: "0.7rem", color: "var(--muted)" }}>{row.support_item_number}</div>
                                                )}
                                            </td>
                                            <td className="mono">{money(row.applied_rate)}</td>
                                            <td>
                                                {row.invoiced
                                                    ? <span className="chip pass">Invoiced</span>
                                                    : row.billed_service_id
                                                        ? <span className="chip info">Allocated</span>
                                                        : <span className="chip warn">Needs allocation</span>}
                                            </td>
                                            <td><button className="icon-btn">{openId === row.shift_id ? "Close" : "Open"}</button></td>
                                        </tr>
                                        {openId === row.shift_id && (
                                            <tr>
                                                <td colSpan={8} style={{ background: "var(--surface-sunk)" }}>
                                                    {error && <div className="error-banner">{error}</div>}
                                                    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end", padding: "0.5rem 0" }}>
                                                        <div className="field" style={{ minWidth: 260, marginBottom: 0 }}>
                                                            <label htmlFor={`al-svc-${row.shift_id}`}>Service</label>
                                                            <select id={`al-svc-${row.shift_id}`} value={draft.serviceId}
                                                                disabled={row.invoiced}
                                                                onChange={(e) => setDraft({ ...draft, serviceId: e.target.value })}>
                                                                <option value="">— Not allocated —</option>
                                                                {(services.data ?? []).filter((s) => s.is_active).map((s) => (
                                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <dl className="kv" style={{ display: "flex", gap: "1.2rem", margin: 0 }}>
                                                            <div>
                                                                <dt>State</dt>
                                                                <dd>{row.pricing_state ?? "WA"}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Category item</dt>
                                                                <dd>{row.pricing_category ? titleCase(row.pricing_category) : "Standard"}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Catalogue</dt>
                                                                <dd className="mono">{money(selectedService(draft.serviceId)?.ndis_item_number ? row.catalogue_price : null)}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Role bill rate</dt>
                                                                <dd className="mono">{money(row.role_rate)}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Applied /h</dt>
                                                                <dd className="mono" style={{ fontWeight: 700 }}>{money(row.applied_rate)}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Rate window</dt>
                                                                <dd>{titleCase(row.rate_category)}</dd>
                                                            </div>
                                                        </dl>
                                                        <div className="field" style={{ width: 120, marginBottom: 0 }}>
                                                            <label htmlFor={`al-km-${row.shift_id}`}>Travel KM</label>
                                                            <input id={`al-km-${row.shift_id}`} type="number" min={0} step={0.1}
                                                                disabled={row.invoiced}
                                                                value={draft.travelKm}
                                                                onChange={(e) => setDraft({ ...draft, travelKm: e.target.value })} />
                                                        </div>
                                                        <div className="spacer" />
                                                        <div style={{ textAlign: "right" }}>
                                                            <div style={{ fontSize: "0.7rem", color: "var(--muted)" }}>Line estimate</div>
                                                            <div className="mono" style={{ fontWeight: 700 }}>
                                                                {money(Number(row.hours) * Number(row.applied_rate))}
                                                            </div>
                                                        </div>
                                                        {!row.invoiced && (
                                                            <button className="btn" disabled={busy} onClick={() => void save(row)}>
                                                                {busy ? "Saving…" : "Update allocation"}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={8} className="empty">
                                        {allocations.loading ? "Loading…" : "No delivered shifts in this window."}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
